import type { FormikHelpers } from "formik";
import { useFormikContext } from "formik";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel";
import React, { useEffect } from "react";
import * as Yup from "yup";

interface PasswordFieldOverride {
	id: string;
	name: string;
	label: string;
}

interface PasswordEntryProps {
	passwordConfirmationDataTestid?: string;
	passwordDataTestid?: string;
	/**
	 * Overrides the default 'password' data field for non-confirmation box
	 */
	passwordOverride?: PasswordFieldOverride;
	setFieldValue?: FormikHelpers<any>["setFieldValue"];
	/**
	 * When true, also renders a "Confirm password" field AND enables the
	 * full strong-password ruleset. When false (the login case), only the
	 * "required" rule is applied so existing accounts with legacy-strength
	 * passwords can still sign in.
	 */
	showConfirmPassword?: boolean;
}

const MINIMUM_PASSWORD_CHARS = 12;

export const PASSWORD_RULES = [
	`must be at least ${MINIMUM_PASSWORD_CHARS} characters`,
	"must contain at least one uppercase and one lowercase letter",
	"must contain at least one number",
	"must contain at least one special character",
	"must be secure - we check against previous password breaches"
]

/**
 * Full strong-password schema. Exported for the rare caller that genuinely
 * needs to re-use the ruleset outside Formik (e.g. backend-returned policy
 * preview). Most callers should NOT pass this into their Yup object — the
 * PasswordEntry control now self-validates via field-level validators.
 */
export const createPasswordSchema = Yup.string()
	.min(MINIMUM_PASSWORD_CHARS, `Password must be at least ${MINIMUM_PASSWORD_CHARS} characters`)
	.max(64, 'Password cannot exceed 64 characters')
	.matches(
		/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
		'Password must contain uppercase, lowercase, and number'
	)
	.matches(
		/^(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).*$/,
		'Password must contain at least one special character'
	)
	.trim()
	.required('Password is required');

export const passwordConfirmationSchema = Yup.string()
	.oneOf([Yup.ref('password')], 'Passwords do not match')
	.required('Please confirm your password');

/**
 * Helper: run a Yup schema synchronously and surface the first error message.
 * Returns undefined when the value is valid — Formik's expected shape for
 * field-level validators.
 */
const runSchema = (schema: Yup.AnySchema, value: unknown): string | undefined => {
	try {
		schema.validateSync(value);
		return undefined;
	} catch (err: any) {
		return err?.message ?? "Invalid value";
	}
};

/**
 * Cross-field check: password should not contain first_name / last_name /
 * email when those fields are present on the same form. Kept as a separate
 * validator so the strong-password schema stays plain Yup.
 */
const containsIdentityField = (
	password: string,
	parent: Record<string, unknown>
): string | undefined => {
	if (!password) {
		return undefined;
	}
	const lower = password.toLowerCase();
	const checks: Array<[string, unknown]> = [
		["first name", parent.first_name],
		["last name", parent.last_name],
		["email", parent.email],
	];
	for (const [label, raw] of checks) {
		if (typeof raw === "string" && raw.length > 0 && lower.includes(raw.toLowerCase())) {
			return `Password must not contain your ${label}`;
		}
	}
	return undefined;
};

/**
 * PasswordEntry renders the password (and optionally confirm password)
 * fields. It self-validates via Formik's field-level `validate` prop, so
 * callers do NOT need to merge `createPasswordSchema` into their own
 * `validationSchema`. Login simply renders <PasswordEntry /> and gets
 * required-only enforcement; signup renders <PasswordEntry showConfirmPassword />
 * and gets the full strong-password + match enforcement automatically.
 */
export const PasswordEntry = ({
	setFieldValue,
	passwordDataTestid,
	passwordConfirmationDataTestid,
	passwordOverride,
	showConfirmPassword = false,
}: PasswordEntryProps) => {
	const { values, touched, validateField } = useFormikContext<Record<string, unknown>>();

	// Cross-field re-validation. Formik runs each field's validator on its
	// own blur, but it doesn't know that `password_confirmation`'s validity
	// depends on `password` (or that identity-field checks on `password`
	// depend on first_name/last_name/email). Without this effect, a user
	// who types a mismatching confirmation, then goes back and fixes the
	// password, would still see the stale "Passwords do not match" error
	// because nothing re-evaluates the already-touched confirm field.
	//
	// We re-validate the *other* field only when it has been touched —
	// avoids prematurely flagging fields the user hasn't interacted with
	// yet (would feel like errors are leaking out of order on first load).
	const passwordValue = (values as any)?.password;
	const confirmValue = (values as any)?.password_confirmation;
	const passwordTouched = (touched as any)?.password;
	const confirmTouched = (touched as any)?.password_confirmation;

	useEffect(() => {
		if (showConfirmPassword && confirmTouched) {
			validateField("password_confirmation");
		}
	}, [passwordValue, showConfirmPassword, confirmTouched, validateField]);

	useEffect(() => {
		if (showConfirmPassword && passwordTouched) {
			validateField("password");
		}
	}, [confirmValue, showConfirmPassword, passwordTouched, validateField]);

	const validatePassword = (value: string): string | undefined => {
		// Login flow: only enforce that something is entered. The actual
		// "is this the right password" check is server-side, surfaced via
		// the page-level FlashError.
		if (!showConfirmPassword) {
			if (!value) {
				return "Password is required";
			}
			return undefined;
		}
		// Signup / reset flow: enforce the full ruleset.
		const schemaError = runSchema(createPasswordSchema, value);
		if (schemaError) {
			return schemaError;
		}
		return containsIdentityField(value, values ?? {});
	};

	const validateConfirmation = (value: string): string | undefined => {
		if (!value) {
			return "Please confirm your password";
		}
		if (value !== (values as any)?.password) {
			return "Passwords do not match";
		}
		return undefined;
	};

	return (
		<>
			<FormikInputWithLabel
				id={passwordOverride?.id ?? "password"}
				label={passwordOverride?.label ?? "Password"}
				name={passwordOverride?.name ?? "password"}
				type="password"
				placeholder="Enter your password"
				className="col xl-12"
				data-testid={passwordDataTestid}
				setFieldValue={setFieldValue}
				validate={validatePassword}
				icon
				autoComplete={showConfirmPassword ? "new-password" : "current-password"}
				aria-required="true"
				required
				guideLine={
					showConfirmPassword
						? `Password must be at least ${MINIMUM_PASSWORD_CHARS} characters long and contain uppercase and lowercase letters, at least one number and one special character. The password will be checked against known data breaches, so must be secure.`
						: undefined
				}
			/>

			{showConfirmPassword && (
				<FormikInputWithLabel
					id="passwordConfirmation"
					label="Confirm Password"
					name="password_confirmation"
					type="password"
					placeholder="Confirm your password"
					className="col xl-12"
					data-testid={passwordConfirmationDataTestid}
					setFieldValue={setFieldValue}
					validate={validateConfirmation}
					icon
					autoComplete={"new-password"}
					aria-required="true"
					required
				/>
			)}
		</>
	);
};
